import type { Dispatch, SetStateAction } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  useRefWrappedCallback,
  useResizeObserver
} from '@elfsight/widget-utils';
import { useInView } from 'react-intersection-observer';
import type { Rect } from 'react-use-rect';
import type { SourceMetaValue } from '../../constants';
import { getSourceMetaValue } from '../../constants';
import type { PostLayoutType } from '../../types';
import type { DataValue, PictureValueType } from '../data/sources';
import { useSocialFeedSettings } from '../utils';
import type { PostDataValue } from './utils';
import { usePostData } from './utils';
import { variations } from './variations';

const HORIZONTAL_WIDTH = 800;

type PopupPostLayoutType = 'popupFeed' | 'popupClassic';

interface PostProps {
  index: number;
  data: DataValue<string | PictureValueType[]>;
  isInPopup?: boolean;
  layout?: PopupPostLayoutType;
  onVisibilityChange?: (visible: boolean, postIndex: number) => void;
}

type PostContextType = PostDataValue & {
  index: number;
  isInPopup: boolean;
  layout: PopupPostLayoutType | PostLayoutType;
  visibility: boolean;
  rect: Rect;
  horizontal: boolean;
  hasMedia: boolean;
  hasTextLink: boolean;
  followButtonLink: string;
  source: DataValue<string | PictureValueType[]>['source'] & SourceMetaValue;
  url: string;
  hasRepost: boolean;
  isReadMoreRendered: boolean;
  setIsReadMoreRendered: Dispatch<SetStateAction<boolean>>;
  repost?: PostDataValue;
  isHovered: boolean;
  setIsHovered: Dispatch<SetStateAction<boolean>>;
  isFirstRow?: boolean;
};

// @ts-expect-error Указание контекста по дефолту не требуется
const PostContext = createContext<PostContextType>({});

export function Post({
  index,
  data,
  isInPopup = false,
  layout: derivedLayout,
  onVisibilityChange: derivedOnVisibilityChange = () => {},
  ...forwardingProps
}: PostProps) {
  const {
    postLayout,
    postShowAuthorName,
    postShowAuthorPicture,
    postShowDate,
    postShowActions,
    postShowText,
    postShowSource,
    layout: widgetLayout
  } = useSocialFeedSettings();
  const onVisibilityChange = useRefWrappedCallback(derivedOnVisibilityChange);
  const [intersectionRef, visibility] = useInView();

  const [isReadMoreRendered, setIsReadMoreRendered] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const measureRef = useRef<HTMLDivElement>(null);
  const [rect] = useResizeObserver(measureRef.current);

  const { source, repost } = data;
  const sourceMetaInfo = getSourceMetaValue(source.type);

  const layout = derivedLayout || postLayout;
  const Layout = variations[layout];

  const [isFirstRow, setIsFirstRow] = useState(false);

  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(visibility, index);
    }
  }, [visibility, index, onVisibilityChange]);

  useEffect(
    () =>
      // Очистка таймера при размонтировании компонента
      () => {
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
        }
      },
    []
  );

  useEffect(() => {
    if (isInPopup || !measureRef.current) {
      return;
    }

    const isMasonry = widgetLayout === 'masonry';

    if (!isMasonry) {
      setIsFirstRow(measureRef.current.offsetTop === 0);

      return;
    }

    const wrapper =
      measureRef.current.parentElement?.parentElement?.parentElement;
    if (!wrapper) {
      return;
    }

    const allCards = Array.from(wrapper.children);

    const minTop = Math.min(
      ...allCards.map((card) => card.getBoundingClientRect().top)
    );
    const currentTop = measureRef.current.getBoundingClientRect().top;

    setIsFirstRow(Math.abs(currentTop - minTop) < 1);
  }, [rect, isInPopup, widgetLayout]);

  const postHasSomeElement =
    postShowAuthorName ||
    postShowAuthorPicture ||
    postShowDate ||
    postShowActions ||
    postShowText ||
    postShowSource;
  const horizontal = postHasSomeElement && rect.width >= HORIZONTAL_WIDTH;

  const postData = usePostData(data)!;
  const repostData = usePostData(repost);
  const hasRepost = !!repost;

  const handleMouseEnter = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    hoverTimerRef.current = setTimeout(() => {
      setIsHovered(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    // Очищаем таймер при уходе курсора
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    setIsHovered(false);
  };

  const value = useMemo<PostContextType>(
    () => ({
      isInPopup,
      layout,
      index,
      visibility,
      rect,
      horizontal,
      hasRepost,
      counters: data.counters,
      title: data.title,
      url: data.source.url,
      repost: hasRepost ? repostData : undefined,
      isReadMoreRendered,
      setIsReadMoreRendered,
      isHovered,
      setIsHovered,
      source: {
        ...source,
        ...sourceMetaInfo
      },
      isFirstRow,
      ...postData
    }),
    [
      isInPopup,
      layout,
      index,
      visibility,
      rect,
      horizontal,
      isReadMoreRendered,
      isHovered,
      hasRepost,
      data.counters,
      data.title,
      data.source.url,
      repostData,
      source,
      sourceMetaInfo,
      postData,
      isFirstRow
    ]
  );

  return (
    <PostContext.Provider value={value}>
      <div
        ref={intersectionRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Layout
          {...forwardingProps}
          ref={measureRef}
          space={horizontal ? 16 : 12}
        />
      </div>
    </PostContext.Provider>
  );
}

export function usePost() {
  return useContext(PostContext);
}
